/**
 * KAN-810: table of the page "Лиды по теплоте". A row is tinted by the level,
 * the name links to the client card, a checkbox column selects rows for the
 * bulk actions ("Выбрать все на странице" in the header).
 * KAN-819: the actions column opens the card or the conversation modal.
 * KAN-927: the wrapper scrolls inside, max-height to the bottom of the window
 * (useFillViewportHeight, as CrmTable), the header stays on top: the
 * horizontal scrollbar is always at the bottom of the window.
 * KAN-961: the height is set by the page column (FillViewportColumn): the
 * table stretches down to the pagination at the bottom of the window.
 */
import { Link } from 'react-router-dom';
import { LeadRowActions } from '@/components/leads/LeadRowActions';
import { LeadTemperatureBadge } from '@/components/leads/LeadTemperatureBadge';
import { CompanyNameLink } from '@/components/clientCompanies/CompanyNameLink';
import { leadTemperatureStyle } from '@/lib/leadTemperature';
import type { LeadSelection } from '@/hooks/useLeadTemperature';
import type { LeadLastMessage, LeadTemperatureRow } from '@/types/leadTemperature';

interface LeadTemperatureTableProps {
  rows: LeadTemperatureRow[];
  selection: LeadSelection;
}

const TH = 'sticky top-0 z-10 bg-gray-50 whitespace-nowrap px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500';
const TD = 'px-3 py-2 align-top';

/** Day and month by Moscow time, "13.10". */
function shortDate(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Moscow' });
}

function lastMessageLine(message: LeadLastMessage): string {
  const who = message.role === 'user' ? 'Клиент' : 'Мы';
  return `${who}: ${message.text}`;
}

function transferLine(row: LeadTemperatureRow): string {
  if (!row.transferred_at && !row.transferred_to) return '';
  return [row.transferred_to, shortDate(row.transferred_at)].filter(Boolean).join(', ');
}

/** Name to show and to use in the checkbox label; never empty. */
function leadDisplayName(row: LeadTemperatureRow): string {
  const telegram = (row.telegram_username || '').trim();
  return (
    (row.name || '').trim() ||
    (row.company || '').trim() ||
    (row.phone || '').trim() ||
    (telegram ? `@${telegram}` : '') ||
    'Без имени'
  );
}

function LeadRow({ row, selection }: { row: LeadTemperatureRow; selection: LeadSelection }) {
  const tint = leadTemperatureStyle(row.temperature)?.row ?? '';
  const transfer = transferLine(row);
  const name = leadDisplayName(row);
  return (
    <tr data-testid="lead-row" className={`border-b border-gray-100 ${tint}`}>
      <td className={TD}>
        <input
          type="checkbox"
          aria-label={`Выбрать ${name}`}
          checked={selection.isSelected(row.id)}
          onChange={() => selection.toggle(row.id)}
          className="h-4 w-4"
        />
      </td>
      <td className={`${TD} min-w-[180px]`}>
        <Link to={`/clients/${row.id}`} className="font-medium text-blue-700 hover:underline">
          {name}
        </Link>
        {row.company && row.company !== name && (
          <div className="text-gray-600">
            <CompanyNameLink company={row.client_company} fallback={row.company} />
          </div>
        )}
        {row.phone && row.phone !== name && <div className="text-gray-600">{row.phone}</div>}
        {row.telegram_username && `@${row.telegram_username}` !== name && <div className="text-gray-500">@{row.telegram_username}</div>}
      </td>
      <td className={TD}>
        <LeadRowActions clientId={row.id} name={name} />
      </td>
      <td className={`${TD} min-w-[180px]`}>
        <LeadTemperatureBadge level={row.temperature} />
        {row.temperature_reason && <div className="mt-1 text-gray-700">{row.temperature_reason}</div>}
      </td>
      <td className={TD}>{row.stage && <span>{row.stage.label}</span>}</td>
      <td className={TD}>{row.manager && <span>{row.manager.name}</span>}</td>
      <td className={`${TD} min-w-[160px]`}>
        {row.lead_attributes.map((attribute, index) => (
          <div key={`${index}-${attribute.name}`} className="text-gray-700">
            {`${attribute.name}: ${attribute.value}`}
          </div>
        ))}
      </td>
      <td className={`${TD} min-w-[200px] max-w-[320px]`}>
        {row.last_message && (
          <p className="line-clamp-3 text-gray-600">{lastMessageLine(row.last_message)}</p>
        )}
      </td>
      <td className={TD}>{transfer && <span className="text-gray-700">{transfer}</span>}</td>
    </tr>
  );
}

export function LeadTemperatureTable({ rows, selection }: LeadTemperatureTableProps) {
  const pageIds = rows.map((row) => row.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selection.isSelected(id));
  return (
    <div
      data-testid="lead-temperature-table"
      className="min-h-0 max-w-full overflow-auto rounded-lg border border-gray-200 bg-white"
    >
      <table className="w-full min-w-[1100px] text-sm">
        <thead>
          <tr>
            <th className={TH}>
              <input
                type="checkbox"
                aria-label="Выбрать все на странице"
                checked={allOnPage}
                onChange={() => selection.togglePage(pageIds)}
                className="h-4 w-4"
              />
            </th>
            <th className={TH}>Клиент</th>
            <th className={TH}>
              <span className="sr-only">Действия</span>
            </th>
            <th className={TH}>Теплота</th>
            <th className={TH}>Этап</th>
            <th className={TH}>Менеджер</th>
            <th className={TH}>Атрибуты</th>
            <th className={TH}>Последнее сообщение</th>
            <th className={TH}>Передан</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <LeadRow key={row.id} row={row} selection={selection} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
